
declare const pdfjsLib: any;
declare const mammoth: any;

/**
 * Robust PDF text extraction that respects document layout and word boundaries.
 * Analyzes gaps between text fragments to decide whether to insert spaces.
 */
const extractTextFromPDF = async (bytes: Uint8Array): Promise<string> => {
  try {
    const loadingTask = pdfjsLib.getDocument({ data: bytes });
    const pdf = await loadingTask.promise;
    let fullText = '';

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      
      // Items in PDF.js have a 'transform' property: [scaleX, skewY, skewX, scaleY, translateX, translateY]
      const items = textContent.items.map((item: any) => ({
        str: item.str,
        x: item.transform[4],
        y: item.transform[5],
        height: item.height || item.transform[0] || 10,
        width: item.width || 0
      }));

      // Group items into lines based on vertical Y coordinate
      const lines: Record<number, any[]> = {};
      const thresholdY = 3; // Pixels to consider items on the same line

      items.forEach((item: any) => {
        let foundLine = false;
        for (const lineY of Object.keys(lines)) {
          const ly = parseFloat(lineY);
          if (Math.abs(item.y - ly) < thresholdY) {
            lines[ly].push(item);
            foundLine = true;
            break;
          }
        }
        if (!foundLine) {
          lines[item.y] = [item];
        }
      });

      // Sort lines top to bottom
      const sortedY = Object.keys(lines)
        .map(Number)
        .sort((a, b) => b - a);

      let pageText = '';
      sortedY.forEach((y) => {
        // Sort fragments in this line from left to right
        const lineItems = lines[y].sort((a, b) => a.x - b.x);
        
        let lineText = '';
        for (let j = 0; j < lineItems.length; j++) {
          const item = lineItems[j];
          const nextItem = lineItems[j + 1];
          
          lineText += item.str;

          if (nextItem) {
            // Calculate the gap between current fragment end and next fragment start
            const gap = nextItem.x - (item.x + item.width);
            
            // Heuristic: If the gap is small (less than 15% of font height), it's likely the same word.
            // If the gap is larger, we insert a space.
            const spaceThreshold = item.height * 0.15;
            
            // Only add a space if there's a real gap and neither fragment already has a space
            if (gap > spaceThreshold && !item.str.endsWith(' ') && !nextItem.str.startsWith(' ')) {
              lineText += ' ';
            }
          }
        }
        pageText += lineText + '\n';
      });

      fullText += pageText + '\n';
    }
    
    // Clean up and apply linguistic fixes for persistent extraction artefacts
    let cleaned = fullText
      .replace(/[ ]{2,}/g, ' ')           // Multiple spaces to single
      .replace(/\n{3,}/g, '\n\n')         // Multiple breaks to double
      // Fix common UK English / Pharm specific split-letter artefacts
      .replace(/(\w) ([sS])\b/g, '$1$2')  // "medicine s" -> "medicines"
      .replace(/\b([iI]) ([sS])\b/g, '$1$2') // "i s" -> "is"
      .replace(/\b([aA]) ([tT])\b/g, '$1$2') // "a t" -> "at"
      .replace(/\b([oO]) ([fF])\b/g, '$1$2') // "o f" -> "of"
      .replace(/(\w) (?=[.,!?;:])/g, '$1') // Remove spaces before punctuation
      .trim();

    return cleaned;
  } catch (err) {
    console.error("PDF extraction error:", err);
    return "Error: Could not extract high-fidelity text from this PDF. It may be an image-only scan.";
  }
};

export const extractTextFromFile = async (base64: string, mimeType: string): Promise<string> => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Handle plain text directly
  if (mimeType === 'text/plain') {
    try {
      return decodeURIComponent(escape(binaryString));
    } catch (e) {
      return binaryString;
    }
  }

  if (mimeType === 'application/pdf') {
    return await extractTextFromPDF(bytes);
  } 
  
  if (mimeType === 'application/json') {
    try {
      const text = new TextDecoder().decode(bytes);
      // Validate it's JSON but return as string for the LLM
      JSON.parse(text);
      return text;
    } catch (err) {
      console.error("JSON parsing error:", err);
      return "Error: Invalid JSON file provided.";
    }
  }

  if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    try {
      const result = await mammoth.extractRawText({ arrayBuffer: bytes.buffer });
      return result.value.trim();
    } catch (err) {
      console.error("Word extraction error:", err);
      return "Error: Could not extract text from this Word document.";
    }
  }

  return "Error: Unsupported file type. Please use PDF or DOCX.";
};
